import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Camera, ChevronRight, ChevronLeft, Save, Globe, Lock, Clock, Flame, Users, Utensils, BookOpen, Upload, Loader2, X, Image } from 'lucide-react';
import { uploadRecipeImage } from '../../services/storageService';

export const RecipeForm = ({ initialData, onSave, onCancel, userId }) => {
  const [step, setStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // Raciones obligatorias: la BD exige 1–50 (migración 20261025110000).
  const [servingsError, setServingsError] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'Fácil',
    time: '',
    servings: 0,
    is_public: true,
    image: 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?q=80&w=800&auto=format&fit=crop',
    ingredients: [{ item: '', amount: 0, unit: '' }],
    steps: [{ id: 1, instruction: '', timerDuration: undefined }],
    tags: []
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        description: initialData.description,
        difficulty: initialData.difficulty,
        time: initialData.time,
        servings: initialData.servings,
        is_public: initialData.is_public,
        image: initialData.image,
        ingredients: initialData.ingredients?.length > 0 ? initialData.ingredients : [{ item: '', amount: 0, unit: '' }],
        steps: initialData.steps?.length > 0 ? initialData.steps : [{ id: 1, instruction: '', timerDuration: undefined }],
        tags: initialData.tags || []
      });
    }
  }, [initialData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const imageUrl = await uploadRecipeImage(file, userId || 'anonymous');
      setFormData({ ...formData, image: imageUrl });
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const addIngredient = () => {
    setFormData({ ...formData, ingredients: [...formData.ingredients, { item: '', amount: 0, unit: '' }] });
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length <= 1) return;
    const newIngs = [...formData.ingredients];
    newIngs.splice(index, 1);
    setFormData({ ...formData, ingredients: newIngs });
  };

  const updateIngredient = (index, field, value) => {
    const newIngs = [...formData.ingredients];
    newIngs[index] = { ...newIngs[index], [field]: value };
    setFormData({ ...formData, ingredients: newIngs });
  };

  const addStep = () => {
    setFormData({ ...formData, steps: [...formData.steps, { id: formData.steps.length + 1, instruction: '', timerDuration: undefined }] });
  };

  const removeStep = (index) => {
    if (formData.steps.length <= 1) return;
    const newSteps = formData.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, id: i + 1 }));
    setFormData({ ...formData, steps: newSteps });
  };

  const updateStep = (index, value) => {
    const newSteps = [...formData.steps];
    newSteps[index].instruction = value;
    setFormData({ ...formData, steps: newSteps });
  };

  const updateStepTimer = (index, minutes) => {
    const newSteps = [...formData.steps];
    newSteps[index].timerDuration = minutes > 0 ? minutes * 60 : undefined;
    setFormData({ ...formData, steps: newSteps });
  };

  const renderStep1 = () => (
    <div className="space-y-10 sm:space-y-12 animate-in slide-in-from-right-4 duration-400">
      <div className="text-center mb-2">
        <h3 className="text-2xl sm:text-3xl font-black heading-serif text-stone-900 dark:text-white mb-1">Detalles Básicos</h3>
        <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm font-medium">Define la identidad de tu creación culinaria.</p>
      </div>

      {/* Image Upload Section */}
      <div className="relative group mx-[-8px] sm:mx-0">
        <div className="h-56 sm:h-72 bg-stone-100 dark:bg-stone-900 rounded-[28px] sm:rounded-[40px] overflow-hidden flex items-center justify-center border-2 border-dashed border-stone-200 dark:border-stone-700 transition-all group-hover:border-primary/50 relative">
          {formData.image && (
            <img 
              src={formData.image} 
              className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" 
              alt="Preview" 
            />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          <input 
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageUpload}
            className="hidden"
            data-testid="image-upload-input"
          />
          
          <div className="relative z-10 flex flex-col items-center gap-4">
            {isUploading ? (
              <div className="bg-white/90 dark:bg-stone-800/90 backdrop-blur-md px-6 py-4 rounded-2xl flex items-center gap-3 shadow-xl">
                <Loader2 size={20} className="animate-spin text-primary" />
                <span className="font-black uppercase tracking-widest text-[10px] text-stone-700 dark:text-white">Subiendo imagen...</span>
              </div>
            ) : (
              <>
                <button 
                  type="button" 
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-white/90 dark:bg-stone-800/90 backdrop-blur-md px-6 py-3 rounded-2xl flex items-center gap-2 font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-primary hover:text-white transition-all text-stone-700 dark:text-white"
                >
                  <Upload size={18} />
                  Subir Foto
                </button>
                
                <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest">
                  JPG, PNG, WebP • Máx 20MB
                </p>
              </>
            )}
          </div>
          
          {formData.image && formData.image !== 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?q=80&w=800&auto=format&fit=crop' && (
            <button
              type="button"
              onClick={() => setFormData({ ...formData, image: 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?q=80&w=800&auto=format&fit=crop' })}
              className="absolute top-4 right-4 p-2 bg-rose-500 text-white rounded-xl hover:bg-rose-600 transition-all shadow-lg z-10"
              title="Eliminar imagen"
            >
              <X size={16} />
            </button>
          )}
        </div>
        
        {uploadError && (
          <div className="mt-4 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-2xl text-rose-600 text-sm font-bold flex items-center gap-2">
            <X size={16} /> {uploadError}
          </div>
        )}
      </div>

      <div className="space-y-8">
        <div>
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">
            <Utensils size={14} className="text-primary" /> Nombre del Plato
          </label>
          <input 
            data-testid="recipe-title-input"
            type="text" 
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="Ej: Pasta Carbonara Cremosa"
            className="w-full p-5 rounded-2xl bg-stone-50 dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold dark:text-white text-lg sm:text-xl placeholder:text-stone-300 dark:placeholder:text-stone-600"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">
             <BookOpen size={14} className="text-primary" /> Historia o Descripción
          </label>
          <textarea 
            data-testid="recipe-description-input"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Cuenta el secreto de esta receta..."
            className="w-full p-5 h-32 sm:h-40 rounded-2xl bg-stone-50 dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none transition-all resize-none font-medium dark:text-white leading-relaxed placeholder:text-stone-300 dark:placeholder:text-stone-600 text-base"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
        <div className="col-span-1">
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">
            <Flame size={14} className="text-primary" /> Dificultad
          </label>
          <select 
            data-testid="recipe-difficulty-select"
            value={formData.difficulty}
            onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
            className="w-full p-5 rounded-2xl bg-stone-50 dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold dark:text-white appearance-none cursor-pointer text-base shadow-sm"
          >
            <option>Fácil</option>
            <option>Media</option>
            <option>Difícil</option>
          </select>
        </div>
        <div className="col-span-1">
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">
            <Clock size={14} className="text-primary" /> Tiempo
          </label>
          <input 
            data-testid="recipe-time-input"
            type="text" 
            value={formData.time}
            onChange={(e) => setFormData({...formData, time: e.target.value})}
            placeholder="Ej: 45 min"
            className="w-full p-5 rounded-2xl bg-stone-50 dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold dark:text-white placeholder:text-stone-300 dark:placeholder:text-stone-600 text-base shadow-sm"
          />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">
            <Users size={14} className="text-primary" /> Raciones
          </label>
          <input 
            data-testid="recipe-servings-input"
            type="number" 
            min={1}
            max={50}
            placeholder="Ej: 4"
            aria-required="true"
            aria-invalid={servingsError || undefined}
            value={formData.servings || ''}
            onChange={(e) => { setServingsError(false); setFormData({...formData, servings: parseInt(e.target.value) || 0}); }}
            className="w-full p-5 rounded-2xl bg-stone-50 dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold dark:text-white placeholder:text-stone-300 dark:placeholder:text-stone-600 text-base shadow-sm"
          />
          {servingsError && (
            <p role="alert" className="mt-2 text-xs font-bold text-rose-600">Indica para cuántas raciones es la receta (1–50).</p>
          )}
        </div>
      </div>

      <div className="p-6 bg-white dark:bg-stone-800/50 rounded-[28px] sm:rounded-[40px] border border-stone-100 dark:border-stone-700 shadow-sm flex items-center justify-between group hover:border-primary/20 transition-all">
        <div className="flex items-center gap-4">
          <div className={`p-3.5 rounded-2xl ${formData.is_public ? 'bg-primary/10 text-primary' : 'bg-stone-100 dark:bg-stone-700 text-stone-400'}`}>
            {formData.is_public ? <Globe size={20}/> : <Lock size={20}/>}
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-stone-900 dark:text-white">Visibilidad: {formData.is_public ? 'Pública' : 'Privada'}</p>
          </div>
        </div>
        <button 
          type="button"
          onClick={() => setFormData({...formData, is_public: !formData.is_public})}
          className={`w-14 h-8 rounded-full transition-all relative shadow-inner ${formData.is_public ? 'bg-primary' : 'bg-stone-200 dark:bg-stone-700'}`}
        >
          <div className={`absolute top-1.5 bg-white w-5 h-5 rounded-full transition-all shadow-md ${formData.is_public ? 'left-8' : 'left-1'}`} />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-10 sm:space-y-12 animate-in slide-in-from-right-4 duration-400">
      <div className="text-center">
        <h3 className="text-2xl sm:text-3xl font-black heading-serif text-stone-900 dark:text-white mb-1">Ingredientes</h3>
        <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm font-medium">Lista todo lo necesario para empezar.</p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {formData.ingredients.map((ing, i) => (
          <div key={i} className="flex flex-col sm:grid sm:grid-cols-[80px_110px_1fr_48px] gap-3 sm:gap-4 items-start sm:items-center bg-stone-50/40 dark:bg-stone-900/40 p-4 sm:p-2 rounded-[24px] sm:rounded-3xl border border-stone-100 dark:border-stone-800 sm:border-none animate-in slide-in-from-bottom-2 duration-300">
            <div className="flex w-full sm:w-auto gap-3">
               <div className="flex-1 sm:w-[80px]">
                 <label className="block sm:hidden text-[9px] font-black uppercase text-stone-400 mb-1 ml-1">Cant</label>
                 <input 
                   type="number" 
                   placeholder="Cant" 
                   className="w-full p-4 rounded-xl sm:rounded-2xl bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold text-sm text-center dark:text-white"
                   value={ing.amount || ''}
                   onChange={(e) => updateIngredient(i, 'amount', parseFloat(e.target.value) || 0)}
                 />
               </div>
               <div className="flex-[2] sm:w-[110px]">
                 <label className="block sm:hidden text-[9px] font-black uppercase text-stone-400 mb-1 ml-1">Unidad</label>
                 <input 
                   type="text" 
                   placeholder="Unidad" 
                   className="w-full p-4 rounded-xl sm:rounded-2xl bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold text-sm text-center dark:text-white placeholder:text-stone-300 dark:placeholder:text-stone-600"
                   value={ing.unit}
                   onChange={(e) => updateIngredient(i, 'unit', e.target.value)}
                 />
               </div>
            </div>
            <div className="w-full">
               <label className="block sm:hidden text-[9px] font-black uppercase text-stone-400 mb-1 ml-1">Ingrediente</label>
               <input 
                 type="text" 
                 placeholder="Nombre del ingrediente..." 
                 className="w-full p-4 rounded-xl sm:rounded-2xl bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold text-sm dark:text-white placeholder:text-stone-300 dark:placeholder:text-stone-600"
                 value={ing.item}
                 onChange={(e) => updateIngredient(i, 'item', e.target.value)}
               />
            </div>
            <button 
              type="button"
              onClick={() => removeIngredient(i)} 
              className={`hidden sm:flex p-3 rounded-xl transition-all ${formData.ingredients.length > 1 ? 'text-stone-300 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20' : 'opacity-20 cursor-not-allowed'}`}
              title="Eliminar ingrediente"
            >
              <Trash2 size={22} />
            </button>
          </div>
        ))}
      </div>

      <button 
        type="button"
        onClick={addIngredient} 
        className="w-full py-5 border-2 border-dashed border-stone-100 dark:border-stone-800 rounded-2xl sm:rounded-3xl text-stone-400 hover:text-primary hover:border-primary/50 transition-all font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 group"
      >
        <Plus size={20} className="group-hover:rotate-90 transition-transform" /> Añadir Ingrediente
      </button>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-10 sm:space-y-12 animate-in slide-in-from-right-4 duration-400">
      <div className="text-center">
        <h3 className="text-2xl sm:text-3xl font-black heading-serif text-stone-900 dark:text-white mb-1">Pasos de Cocinado</h3>
        <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm font-medium">Guía a otros chefs paso a paso.</p>
      </div>

      <div className="space-y-12 relative before:absolute before:left-5 sm:before:left-8 before:top-4 before:bottom-4 before:w-0.5 before:bg-stone-100 dark:before:bg-stone-800">
        {formData.steps.map((s, i) => (
          <div key={i} className="relative pl-11 sm:pl-20 group animate-in slide-in-from-bottom-2 duration-300">
            <div className="absolute left-0 top-0 w-10 sm:w-16 h-10 sm:h-16 bg-white dark:bg-stone-900 border-2 border-stone-100 dark:border-stone-800 rounded-2xl sm:rounded-[24px] flex items-center justify-center font-black text-xs sm:text-xl text-stone-900 dark:text-white shadow-sm z-10 group-hover:border-primary group-hover:text-primary transition-all">
              {i + 1}
            </div>
            
            <div className="p-6 sm:p-10 bg-stone-50 dark:bg-stone-900/50 rounded-[32px] sm:rounded-[48px] border border-stone-100 dark:border-stone-800 focus-within:ring-4 focus-within:ring-primary/5 transition-all space-y-6">
              <div className="space-y-4">
                <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">
                  Instrucción
                </label>
                <textarea 
                  value={s.instruction}
                  onChange={(e) => updateStep(i, e.target.value)}
                  placeholder="Explica qué hacer en este paso..."
                  className="w-full bg-white dark:bg-stone-950 p-6 rounded-[24px] border border-stone-100 dark:border-stone-800 outline-none resize-none text-stone-700 dark:text-stone-300 font-medium leading-relaxed min-h-[120px] placeholder:text-stone-300 dark:placeholder:text-stone-600 text-base shadow-sm focus:border-primary/30"
                />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-6 items-end">
                <div className="w-full sm:w-1/2">
                  <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">
                    <Clock size={14} className="text-primary" /> Temporizador (minutos)
                  </label>
                  <input 
                    type="number" 
                    min="0"
                    step="0.5"
                    placeholder="Ej: 5 (Opcional)"
                    value={s.timerDuration ? s.timerDuration / 60 : ''}
                    onChange={(e) => updateStepTimer(i, parseFloat(e.target.value) || 0)}
                    className="w-full p-4 rounded-2xl bg-white dark:bg-stone-950 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold text-sm dark:text-white placeholder:text-stone-300 dark:placeholder:text-stone-600 shadow-sm"
                  />
                </div>
                
                <div className="w-full sm:w-1/2 flex justify-end">
                  <button 
                    type="button"
                    onClick={() => removeStep(i)} 
                    className={`flex items-center gap-2 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all bg-white dark:bg-stone-950 border border-stone-100 dark:border-stone-800 ${formData.steps.length > 1 ? 'text-stone-300 hover:text-rose-500 hover:border-rose-100' : 'opacity-20 cursor-not-allowed'}`}
                  >
                    <Trash2 size={16} /> Eliminar Paso
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button 
        type="button"
        onClick={addStep} 
        className="w-full py-6 border-2 border-dashed border-stone-100 dark:border-stone-800 rounded-[32px] sm:rounded-[48px] text-stone-400 hover:text-primary hover:border-primary/50 transition-all font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 group"
      >
        <Plus size={24} className="group-hover:rotate-90 transition-transform" /> Añadir Nuevo Paso
      </button>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto pb-40 px-0.5 sm:px-4">
      <div className="flex items-center justify-between mb-8 sm:mb-16 sticky top-20 sm:top-24 z-20 bg-base-white/95 dark:bg-base-dark/95 backdrop-blur-xl p-1.5 sm:p-4 mx-0.5 sm:mx-0 rounded-[22px] sm:rounded-[40px] border border-stone-100 dark:border-stone-800 shadow-lg">
        <button type="button" onClick={onCancel} className="text-stone-500 dark:text-stone-400 font-black px-4 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 rounded-full transition-all uppercase text-[9px] tracking-[0.1em] sm:tracking-[0.2em]">Cerrar</button>
        
        <div className="flex gap-3 sm:gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex flex-col items-center gap-1.5 sm:gap-2">
              <div className={`w-8 sm:w-16 h-2 rounded-full transition-all duration-500 ${step >= i ? 'bg-primary' : 'bg-stone-100 dark:bg-stone-800'}`} />
              <span className={`text-[8px] font-black uppercase tracking-widest transition-colors ${step === i ? 'text-primary' : 'text-stone-300 dark:text-stone-600'}`}>
                {i === 1 ? 'Básicos' : i === 2 ? 'Ingrs' : 'Pasos'}
              </span>
            </div>
          ))}
        </div>

        {step === 3 ? (
          <button 
            data-testid="save-recipe-btn"
            type="button"
            onClick={() => {
              const s = formData.servings;
              if (!(Number.isInteger(s) && s >= 1 && s <= 50)) { setServingsError(true); setStep(1); return; }
              onSave(formData);
            }}
            className="group relative bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 px-5 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-2xl hover:bg-primary dark:hover:bg-primary dark:hover:text-white transition-all active:scale-95 flex items-center gap-2 overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-2"><Save size={16} /> <span className="hidden sm:inline">{initialData ? 'Actualizar' : 'Publicar'}</span><span className="sm:hidden">OK</span></span>
            <div className="absolute inset-0 bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ) : (
          <button 
            data-testid="next-form-step-btn"
            type="button"
            onClick={() => setStep(step + 1)} 
            className="bg-primary text-white px-5 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black shadow-xl shadow-primary/20 flex items-center gap-2 text-[10px] uppercase tracking-widest hover:bg-orange-600 transition-all active:scale-95"
          >
            <span className="hidden sm:inline">Continuar</span> <ChevronRight size={16} />
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-surface-dark p-3.5 sm:p-14 rounded-[28px] sm:rounded-[56px] shadow-sm border border-stone-100 dark:border-stone-800 min-h-[500px] sm:min-h-[700px] mx-0 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-80 h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 w-full overflow-x-hidden">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>

      {step > 1 && (
        <div className="mt-8 sm:mt-12 flex justify-center">
          <button 
            type="button"
            onClick={() => setStep(step - 1)} 
            className="group flex items-center gap-3 text-stone-300 dark:text-stone-500 font-black hover:text-stone-900 dark:hover:text-white uppercase text-[9px] tracking-[0.2em] transition-all"
          >
            <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" /> Volver atrás
          </button>
        </div>
      )}
    </div>
  );
};
